import { Badge } from "@/components/ui/badge";
import type { PbxRegistrationState } from "@/features/connectors/types";
import type { Tone } from "@/features/utils/colors";
import { useTranslation } from "@/features/utils/i18n";

const TONES: Record<PbxRegistrationState["state"], Tone> = {
  connected: "green",
  connecting: "blue",
  rejected: "red",
  unreachable: "red",
  unknown: "gray",
};

/**
 * Is HeyUp registered on the client's PBX? What Asterisk last reported, read by
 * the backend (`connectors/pbx-status.ts`). "Rejected" and "unreachable" are
 * told apart there — the PBX refusing our credentials, or not answering — and
 * say different things to whoever has to fix it.
 */
export const RegistrationBadge = ({
  registration,
  withHint = false,
}: {
  registration?: PbxRegistrationState;
  withHint?: boolean;
}) => {
  const { t, i18n } = useTranslation("connectors");
  if (!registration) return null;

  const since = registration.since
    ? new Date(registration.since).toLocaleString(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      })
    : null;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={TONES[registration.state]}>
          {t(`connectors.sip.registration.${registration.state}`)}
        </Badge>
        {since && (
          <span className="text-xs text-muted-foreground">
            {t("connectors.sip.registration.since", { since })}
          </span>
        )}
      </div>
      {withHint && registration.state !== "connected" && (
        <p className="text-xs text-muted-foreground">
          {t(`connectors.sip.registration.hint.${registration.state}`)}
        </p>
      )}
    </div>
  );
};
